import getIncluded from '../../japi/getIncluded';
import getMultipleIncluded from '../../japi/getMultipleIncluded';
import { JapiDocument } from '../../japi/types/Document';
import { DepartmentResource } from '../Department/Department';
import { transformToDepartment } from '../Department/DepartmentTransformers';
import { DepartmentGroupResource } from '../DepartmentGroup/DepartmentGroup';
import { ExperienceResource } from '../Experience/Experience';
import { transformToExperience } from '../Experience/ExperienceTransformers';
import { PersonResource } from '../Person/Person';
import { transformToPerson } from '../Person/PersonTransformers';
import { ExchangeShift, ShiftResource } from '../Shift/Shift';
import { transformToExchangeShift, transformToShiftPlanningShift } from '../Shift/ShiftTransformers';
import { ShiftPlanningResource, ShiftPlanningWithShift } from '../ShiftPlanning/ShiftPlanning';
import {
    transformToShiftPlanning,
    transformToShiftPlanningWithShift,
} from '../ShiftPlanning/ShiftPlanningTransformers';
import { ExchangeUser, ExchangeUserResource, ShiftIndexUserResource } from '../User/User';
import { transformToExchangeUser, transformToShiftPlanningUser } from '../User/UserTransformers';
import {
    Exchange,
    ExchangeResource,
    ShiftExchange,
    ShiftExchangeResource,
} from './Exchange';

export const transformExchangeResource = (
    exchange: ExchangeResource,
    owner: ExchangeUser,
    replacementUsers: ExchangeUser[],
    shift: ExchangeShift,
): Exchange => ({
    id: exchange.id,
    type: exchange.type,
    createdAt: new Date(exchange.attributes.createdAt),
    replacementUsers,
    shiftStatusForOwner: 'unknown',
    owner,
    shift,
});

export const transformShiftExchangeResource = (
    shiftExchange: ShiftExchangeResource,
    shiftPlanning1: ShiftPlanningWithShift,
    shiftPlanning2: ShiftPlanningWithShift,
): ShiftExchange => ({
    id: shiftExchange.id,
    type: shiftExchange.type,
    createdAt: new Date(shiftExchange.attributes.createdAt),
    shiftPlanning1,
    shiftPlanning2,
});

export const getExchangeFromApiData = (
    doc: JapiDocument,
    exchangeResource: ExchangeResource,
) => {
    const ownerResource = getIncluded<ExchangeUserResource>(doc, exchangeResource, 'owner');
    if (!ownerResource) throw Error(`No owner found for exchange ${exchangeResource.id}.`);

    const ownerPersonResource = getIncluded<PersonResource>(doc, ownerResource, 'person');
    if (!ownerPersonResource) throw Error(`No person found for owner ${ownerResource.id}.`);
    const ownerPerson = transformToPerson(ownerPersonResource);

    const ownerExperienceResource = getIncluded<ExperienceResource>(doc, ownerResource, 'experience');
    if (!ownerExperienceResource) throw Error(`No experience found for owner ${ownerResource.id}.`);
    const ownerExperience = transformToExperience(ownerExperienceResource);

    const owner = transformToExchangeUser(ownerResource, ownerPerson, ownerExperience);

    const shiftResource = getIncluded<ShiftResource>(doc, exchangeResource, 'shift');
    if (!shiftResource) throw Error(`No shift found for exchange ${exchangeResource.id}.`);

    const shiftDepartmentResource = getIncluded<DepartmentResource>(doc, shiftResource, 'department');
    if (!shiftDepartmentResource) throw Error(`No department found for shift ${shiftResource.id}`);
    const shiftDepartmentDepartmentGroupResource = getIncluded<DepartmentGroupResource>(doc, shiftDepartmentResource, 'group');
    const shiftDepartment = transformToDepartment(shiftDepartmentResource, shiftDepartmentDepartmentGroupResource);

    const shiftShiftPlanningResources = getMultipleIncluded<ShiftPlanningResource>(doc, shiftResource, 'shiftPlannings');
    if (!shiftShiftPlanningResources) throw Error(`No shiftPlannings found for shift ${shiftResource.id}`);
    const shiftShiftPlannings = shiftShiftPlanningResources.map(shiftPlanningResource => {
        const shiftPlanningUserResource = getIncluded<ShiftIndexUserResource>(doc, shiftPlanningResource, 'user');
        if (!shiftPlanningUserResource) throw Error(`No user found for shiftPlanning ${shiftPlanningResource.id}`);

        const shiftPlanningUserExperienceResource = getIncluded<ExperienceResource>(doc, shiftPlanningUserResource, 'experience');
        if (!shiftPlanningUserExperienceResource) throw Error(`No experience found for shiftPlanningUser ${shiftPlanningUserResource.id}`);
        const shiftPlanningUserExperience = transformToExperience(shiftPlanningUserExperienceResource);

        const shiftPlanningUser = transformToShiftPlanningUser(shiftPlanningUserResource, undefined, shiftPlanningUserExperience);

        return transformToShiftPlanning(shiftPlanningResource, shiftPlanningUser);
    });

    const shift = transformToExchangeShift(shiftResource, shiftDepartment, shiftShiftPlannings);

    const replacementUserResources = getMultipleIncluded<ExchangeUserResource>(doc, exchangeResource, 'replacementUsers');
    if (!replacementUserResources) throw Error(`No replacementUsers found for exchange ${exchangeResource.id}.`);
    const replacementUsers = replacementUserResources.map(replacementUserResource => {
        const replacementUserPersonResource = getIncluded<PersonResource>(doc, replacementUserResource, 'person');
        if (!replacementUserPersonResource) throw Error(`No person found for replacementUser ${replacementUserResource.id}.`);
        const replacementUserPerson = transformToPerson(replacementUserPersonResource);

        const replacementUserExperienceResource = getIncluded<ExperienceResource>(doc, replacementUserResource, 'experience');
        if (!replacementUserExperienceResource) throw Error(`No experience found for replacementUser ${replacementUserResource.id}.`);
        const replacementUserExperience = transformToExperience(replacementUserExperienceResource);

        return transformToExchangeUser(replacementUserResource, replacementUserPerson, replacementUserExperience);
    });

    return transformExchangeResource(exchangeResource, owner, replacementUsers, shift);
};

export const getShiftExchangeFromApiData = (
    doc: JapiDocument,
    shiftExchangeResource: ShiftExchangeResource,
) => {
    const [shiftPlanning1, shiftPlanning2] = ['shiftPlanning1', 'shiftPlanning2'].map(key => {
        const shiftPlanningResource = getIncluded<ShiftPlanningResource>(doc, shiftExchangeResource, key);
        if (!shiftPlanningResource) throw Error(`shiftPlanning not found for ${key}`);

        const shiftPlanningUserResource = getIncluded<ShiftIndexUserResource>(doc, shiftPlanningResource, 'user');
        if (!shiftPlanningUserResource) throw Error(`shiftPlanning user not found for ${key}`);

        const shiftPlanningUserExperienceResource = getIncluded<ExperienceResource>(doc, shiftPlanningUserResource, 'experience');
        if (!shiftPlanningUserExperienceResource) throw Error(`shiftPlanning user experience not found for ${key}`);
        const shiftPlanningUserExperience = transformToExperience(shiftPlanningUserExperienceResource);

        const shiftPlanningUser = transformToShiftPlanningUser(shiftPlanningUserResource, undefined, shiftPlanningUserExperience);

        const shiftPlanningShiftResource = getIncluded<ShiftResource>(doc, shiftPlanningResource, 'shift');
        if (!shiftPlanningShiftResource) throw Error(`shift not found for shiftPlanning ${shiftPlanningResource.id}`);

        const shiftPlanningShiftDepartmentResource = getIncluded<DepartmentResource>(doc, shiftPlanningShiftResource, 'department');
        if (!shiftPlanningShiftDepartmentResource) throw Error(`department not found for shift ${shiftPlanningShiftResource.id}`);
        const shiftPlanningShiftDepartmentDepartmentGroupResource = getIncluded<DepartmentGroupResource>(doc, shiftPlanningShiftDepartmentResource, 'group');
        const shiftPlanningShiftDepartment = transformToDepartment(shiftPlanningShiftDepartmentResource, shiftPlanningShiftDepartmentDepartmentGroupResource);

        const shiftPlanningShift = transformToShiftPlanningShift(shiftPlanningShiftResource, shiftPlanningShiftDepartment);

        return transformToShiftPlanningWithShift(shiftPlanningResource, shiftPlanningUser, shiftPlanningShift);
    });

    return transformShiftExchangeResource(
        shiftExchangeResource,
        shiftPlanning1,
        shiftPlanning2,
    );
};
